/**
 * Default pure-tone screening configuration.
 * The user adjusts the tone with +/- buttons (adjustStepDb per press) until it
 * is only just audible; that level is the threshold. Levels are attenuations
 * below the loudest presentation, so a HIGHER threshold means the person heard
 * a quieter tone (better). This is a relative screening: consumer headphones
 * are not calibrated, so results are not clinical dB HL.
 */
export const DEFAULT_PURETONE_CONFIG = {
    frequencies: [500, 1000, 2000, 4000, 6000],
    ears: ["left", "right"],
    startAttenuationDb: 10,
    maxAttenuationDb: 60,
    adjustStepDb: 5,
    toneSeconds: 1.0,
};
/** Ordered (ear, frequency) plan for the full test. */
export function tonePlan(config) {
    const plan = [];
    for (const ear of config.ears) {
        for (const frequency of config.frequencies) {
            plan.push({ ear, frequency });
        }
    }
    return plan;
}
export function emptyAudiogram() {
    return { left: {}, right: {} };
}
export function audiogramFromTrials(trials, config) {
    const ag = emptyAudiogram();
    // Threshold per (ear, freq) = softest (max attenuation) level actually heard.
    for (const t of trials) {
        if (!t.heard)
            continue;
        const key = String(t.frequency);
        const bucket = ag[t.ear];
        if (bucket[key] == null || t.attenuationDb > bucket[key]) {
            bucket[key] = t.attenuationDb;
        }
    }
    // Ensure every tested point exists (0 if never heard).
    for (const ear of config.ears) {
        for (const f of config.frequencies) {
            const key = String(f);
            if (ag[ear][key] == null)
                ag[ear][key] = 0;
        }
    }
    return ag;
}
/**
 * Coarse interpretation: count tested points where the person needed a loud
 * tone (low attenuation threshold). Used only for a soft, non-diagnostic note.
 */
export function toneConcernCount(audiogram, config, concernBelowDb = 15) {
    let count = 0;
    for (const ear of config.ears) {
        for (const f of config.frequencies) {
            const v = audiogram[ear][String(f)];
            if (v != null && v < concernBelowDb)
                count += 1;
        }
    }
    return count;
}
/**
 * Map an audiogram to a screening category. "Concern points" are frequencies
 * where the person only heard a relatively loud tone. This is a screening
 * heuristic, not a clinical diagnosis.
 */
export function categorizeAudiogram(audiogram, config) {
    const concerns = toneConcernCount(audiogram, config);
    if (concerns === 0)
        return "good";
    if (concerns <= 3)
        return "fair";
    if (concerns <= 6)
        return "poor";
    return "severe";
}
/** Concern points (loud-tone frequencies) for a single ear. */
export function earConcernCount(audiogram, ear, config, concernBelowDb = 15) {
    let count = 0;
    for (const f of config.frequencies) {
        const v = audiogram[ear][String(f)];
        if (v != null && v < concernBelowDb)
            count += 1;
    }
    return count;
}
/**
 * Per-ear screening category. Same heuristic as categorizeAudiogram but scored
 * over a single ear's frequencies (5 tested points by default). Screening
 * heuristic, not a clinical diagnosis.
 */
export function categorizeEar(audiogram, ear, config) {
    const concerns = earConcernCount(audiogram, ear, config);
    if (concerns === 0)
        return "good";
    if (concerns === 1)
        return "fair";
    if (concerns <= 3)
        return "poor";
    return "severe";
}
